/**
 * Qué campo puede ir en un gráfico y cuántas métricas admite cada tipo de panel.
 *
 * Puro (sin React): es donde viven los tests.
 *
 * La regla de la dimensión sale del CATÁLOGO, no del nombre del campo. Cada dimensión de
 * `FIELD_DATA_BY_ID` viaja al navegador con su `data_type` (`FieldsDataService.get_fields_data`),
 * y tres de ellas no se pueden graficar: «Imagen/video» (IMAGE), «Link de preview compartible» y
 * «URL final del anuncio» (URL) — las tres del grupo Anuncio de Meta. Buscar «url» o «link» en el
 * nombre acertaría en español y fallaría en inglés; el `data_type` es el dato.
 *
 * Motivo: la torta nacía en la ÚLTIMA dimensión, así que con «Meta - Anuncios» arrancaba en
 * Imagen/video y las URLs desbordaban la tarjeta por encima del selector (card PlFYrmTo).
 */
/**
 * Los `data_type` que no se pueden usar como dimensión de un gráfico.
 *
 * ⚠️ Esta lista está DUPLICADA en `reports/services/layout_schema.py` (`NON_CHART_DATA_TYPES`) y
 * nada las concilia: no hay endpoint que las publique, y el mapa `DATA_TYPES` que el backend sí
 * manda en el catálogo **no incluye** `URL` ni `IMAGE`. Se lee el valor crudo de `data_type` a
 * propósito; si alguien pasa a resolverlo contra ese mapa, el filtro deja de filtrar en silencio
 * (review C-editor-07c). Si acá cambia, allá también.
 */
export const NON_CHART_DATA_TYPES = Object.freeze(['IMAGE', 'URL']);

/** El `data_type` del catálogo para un id de campo, o `null` si no lo conoce. */
export function fieldDataType(fieldId, fieldsData) {
    if (!fieldId) return null;
    const byId = (fieldsData && fieldsData.FIELD_DATA_BY_ID) || {};
    const field = byId[fieldId];
    return (field && field.data_type) || null;
}

/** Si una dimensión sirve como eje de un gráfico. Sin catálogo se asume que sí. */
export function isChartableDimension(fieldId, fieldsData) {
    const dataType = fieldDataType(fieldId, fieldsData);
    return !dataType || !NON_CHART_DATA_TYPES.includes(dataType);
}

/**
 * La dimensión con la que nace un gráfico: la PRIMERA que se puede graficar.
 *
 * Si ninguna califica —una plantilla de puras URLs— devuelve la primera igual: el panel tiene que
 * nacer con algo y el usuario puede cambiarlo. Nunca devuelve `undefined`.
 */
export function preferredChartDimension(dimensions, fieldsData) {
    const list = dimensions || [];
    if (!list.length) return null;
    const chartable = list.find((d) => isChartableDimension(d, fieldsData));
    return chartable || list[0];
}

/**
 * Cuántas métricas admite cada gráfico. Una sola definición para el control y su aviso.
 *
 * Barras 4 (es lo que permitía el Explorador clásico); evolución 6, que ocupa las 12 columnas y
 * aguanta más series. La torta y la métrica destacada llevan exactamente una: lo exige el
 * validador del backend (`reports/services/layout_schema.py`).
 */
// Los tipos van como literales y no como `PANEL_TYPES`: `informeModel` importa de acá para
// elegir la dimensión default, y importarlo de vuelta armaría un ciclo.
export const METRICS_LIMIT = Object.freeze({bars: 4, line: 6, pie: 1, kpi: 1});

export function metricsLimitFor(type) {
    return METRICS_LIMIT[type] || 1;
}
